import { type Coordinate, type GoogleGeocoderAddressComponent, type GoogleLatLng, getDayInMillisAtSpecificTime, type TimestampInMillis } from '@cobliteam/common-types';
import moment from 'moment';

import type {
  ActivitiesSpreadsheetImportFormValues,
  ActivityVehicleAssociation,
  ActivityVehicleAssociationAPI,
  DeleteRoutes,
  DeleteRoutesAPI,
  EditActivity,
  EditActivityAPI,
  EditRoute,
  EditRouteAPI,
  InputActivity,
  InputActivityAPI,
  InputDestination,
  InputDestinationAPI,
  NewActivity,
  NewActivityAPI,
  NewDestination,
  NewDestinationAPI,
  NewRoute,
  NewRouteAPI,
  ParsedActivitiesSpreadsheetRowAPI,
  RouteBreak,
  RouteBreakAPI,
  RouteRecurrency,
  RouteRecurrencyAPI,
  RouteSettings,
  RouteSettingsAPI,
  RouteWorkingHours,
  RouteWorkingHoursAPI,
  RoutingOptimization,
  RoutingOptimizationAPI,
  RoutingSettings,
  RoutingSettingsAPI,
  TimeWindow,
} from '../Definitions';
import { concatSpreadsheetColumns, mapAddressComponents } from '../utils';

// Routing settings
const routeWorkingHoursAPITransformer = (routeWorkingHours: RouteWorkingHours): RouteWorkingHoursAPI => {
  const { startTime, endTime } = routeWorkingHours;

  return {
    start_time: startTime,
    end_time: endTime,
  };
};

const routeBreakAPITransformer = (routeBreak: RouteBreak): RouteBreakAPI => {
  const { startTime, endTime, duration } = routeBreak;

  return {
    start_time: startTime,
    end_time: endTime,
    duration,
  };
};

const inputDestinationAPITransformerFromGooglePlaces = (location: GoogleLatLng, addressComponents: GoogleGeocoderAddressComponent[]): InputDestinationAPI => {
  const {
    administrative_area_level_2: city,
    country,
    subpremise: streetComplement,
    sublocality_level_1: neighborhood,
    postal_code: postalCode,
    administrative_area_level_1: state,
    route: streetAddress,
    street_number: streetNumber,
  } = mapAddressComponents(addressComponents);

  return {
    coordinate: {
      latitude: location.lat(),
      longitude: location.lng(),
    },
    city,
    country,
    street_complement: streetComplement,
    neighborhood,
    postal_code: postalCode,
    state,
    street_address: streetAddress,
    street_number: streetNumber,
  };
};

const inputDestinationAPITransformerFromInputDestination = (inputDestination: InputDestination): InputDestinationAPI => {
  const { coordinate, city, country, neighborhood, postalCode, state, streetAddress, streetComplement, streetNumber } = inputDestination;

  return {
    coordinate: {
      latitude: coordinate.latitude,
      longitude: coordinate.longitude,
    },
    city,
    country,
    street_complement: streetComplement,
    neighborhood,
    postal_code: postalCode,
    state,
    street_address: streetAddress,
    street_number: streetNumber,
  };
};

export const inputActivityWithoutRouteAPITransformer = (inputActivity: InputActivity): InputActivityAPI | undefined => {
  const { destination, name, additionalInfo, phoneNumber, startTime, deviceId, driverId, driverName, vehicleId } = inputActivity;

  const { geometry, address_components: addressComponents } = destination;

  if (geometry && addressComponents) {
    return {
      name,
      destination: geometry.location && inputDestinationAPITransformerFromGooglePlaces(geometry.location, addressComponents),
      start_time: startTime,
      phone_number: phoneNumber,
      additional_info: additionalInfo,
      device_id: deviceId,
      driver_id: driverId,
      driver_name: driverName,
      vehicle_id: vehicleId,
    };
  }

  return undefined;
};

export const inputActivitiesAPITransformer = (activities: InputActivity[]): (InputActivityAPI | undefined)[] => {
  return activities.map((activity) => inputActivityWithoutRouteAPITransformer(activity)).filter((activity) => activity !== undefined);
};

const inputActivityAPITransformer = (inputActivity: InputActivity, routeStartTime: TimestampInMillis): InputActivityAPI | undefined => {
  const { destination, duration, name, startTimeWindow, endTimeWindow, additionalInfo, phoneNumber, loadSize, startTime } = inputActivity;
  const { geometry, address_components: addressComponents } = destination;

  const startTimeAtTheFollowingDay = moment(routeStartTime).add(1, 'day').valueOf();

  const timeWindows: TimeWindow[] | undefined = startTimeWindow
    ? [
        {
          earliest: getDayInMillisAtSpecificTime(routeStartTime, startTimeWindow),
          latest: getDayInMillisAtSpecificTime(routeStartTime, endTimeWindow || startTimeWindow),
        },
        {
          earliest: getDayInMillisAtSpecificTime(startTimeAtTheFollowingDay, startTimeWindow),
          latest: getDayInMillisAtSpecificTime(startTimeAtTheFollowingDay, endTimeWindow || startTimeWindow),
        },
      ]
    : undefined;

  if (geometry && addressComponents) {
    return {
      duration,
      name,
      load_size: loadSize,
      time_windows: timeWindows,
      additional_info: additionalInfo,
      start_time: startTime,
      destination: geometry.location && inputDestinationAPITransformerFromGooglePlaces(geometry.location, addressComponents),
      phone_number: phoneNumber,
    };
  }
};

const routingOptimizationAPITransformer = (routingOptimization: RoutingOptimization): RoutingOptimizationAPI => {
  const { objectives, workingHours, numVehicles, endRouteDestination, vehicleAverageCargoCapacity, break: routeBreak } = routingOptimization;

  return {
    working_hours: routeWorkingHoursAPITransformer(workingHours),
    num_vehicles: numVehicles,
    objectives,
    end_route_destination: endRouteDestination && inputDestinationAPITransformerFromInputDestination(endRouteDestination),
    break: routeBreak ? routeBreakAPITransformer(routeBreak) : undefined,
    vehicle_average_cargo_capacity: vehicleAverageCargoCapacity,
  };
};

export const routingRoutingSettingsAPITransformer = (routingSettings: RoutingSettings): RoutingSettingsAPI => {
  const { activities, startTime, type, optimization } = routingSettings;

  const reduceInputActivitiesToAPI = (reducedActivities: InputActivityAPI[], activity: InputActivity) => {
    const inputActivityAPI = inputActivityAPITransformer(activity, startTime);
    if (inputActivityAPI) {
      reducedActivities.push(inputActivityAPI);
    }
    return reducedActivities;
  };

  return {
    activities: activities.reduce(reduceInputActivitiesToAPI, []),
    start_time: startTime,
    type,
    optimization: optimization ? routingOptimizationAPITransformer(optimization) : undefined,
  };
};

// Route transformers
const coordinateAPITransformer = ({ latitude, longitude }: Coordinate) => ({
  latitude,
  longitude,
});

const destinationAPITransformer = (destination: NewDestination): NewDestinationAPI => {
  const { city, coordinate, country, neighborhood, postalCode, state, streetAddress, streetComplement, streetNumber } = destination;

  return {
    city,
    coordinate: coordinateAPITransformer(coordinate),
    country,
    neighborhood,
    postal_code: postalCode,
    state,
    street_address: streetAddress,
    street_complement: streetComplement,
    street_number: streetNumber,
  };
};

export const activityAPITransformer = (activity: NewActivity): NewActivityAPI => {
  const {
    id,
    type,
    visitStatus,
    completionStatus,
    position,
    destination,
    createdAt,
    updatedAt,
    name,
    plannedDuration,
    plannedDistanceInMeters,
    startTime,
    endTime,
    timeWindows,
    waitingTime,
    additionalInfo,
    executedDuration,
    arrivalTime,
    phoneNumber,
    deviceId,
    driverId,
    driverName,
    routeId,
    vehicleId,
    loadSize,
    executedEndTime,
  } = activity;

  return {
    id,
    type,
    visit_status: visitStatus,
    completion_status: completionStatus,
    position,
    destination: destinationAPITransformer(destination),
    created_at: createdAt,
    updated_at: updatedAt,
    name,
    planned_duration: plannedDuration,
    planned_distance_in_meters: plannedDistanceInMeters,
    start_time: startTime,
    end_time: endTime,
    time_windows: timeWindows,
    waiting_time: waitingTime,
    additional_info: additionalInfo,
    executed_duration: executedDuration,
    arrival_time: arrivalTime,
    phone_number: phoneNumber,
    device_id: deviceId,
    driver_id: driverId,
    driver_name: driverName,
    route_id: routeId,
    vehicle_id: vehicleId,
    load_size: loadSize,
    executed_end_time: executedEndTime,
  };
};

const routeSettingsAPITransformer = (routeSettings: RouteSettings): RouteSettingsAPI => {
  const { breakWindow, objectives, routingType, workingHours } = routeSettings;

  return {
    break_window: breakWindow && routeBreakAPITransformer(breakWindow),
    objectives,
    routing_type: routingType,
    working_hours: routeWorkingHoursAPITransformer(workingHours),
  };
};

const routeRecurrencyAPITransformer = (routeRecurrency: RouteRecurrency): RouteRecurrencyAPI => {
  const { id, frequency, repeatInterval, daysOfWeek, daysOfMonth, untilDate, weekNumbers } = routeRecurrency;

  return {
    id,
    repeat_interval: repeatInterval,
    frequency,
    days_of_month: daysOfMonth,
    week_numbers: weekNumbers,
    days_of_week: daysOfWeek,
    until_date: untilDate,
  };
};

export const routeAPITransformer = (route: NewRoute): NewRouteAPI => {
  const {
    id,
    name,
    activities,
    points,
    type,
    status,
    plannedDuration,
    plannedDistanceInMeters,
    createdAt,
    updatedAt,
    startTime,
    endTime,
    vehicleId,
    driverId,
    deviceId,
    settings,
    driverName,
    break: routeBreak,
    recurrency,
    propagationType: updatePropagationType,
    executedEndTime,
    executedStartTime,
  } = route;

  return {
    id,
    name,
    activities: activities.map(activityAPITransformer),
    points: points?.map(coordinateAPITransformer),
    type,
    status,
    planned_duration: plannedDuration,
    planned_distance_in_meters: plannedDistanceInMeters,
    created_at: createdAt,
    updated_at: updatedAt,
    start_time: startTime,
    end_time: endTime,
    vehicle_id: vehicleId,
    driver_id: driverId,
    device_id: deviceId,
    driver_name: driverName,
    break: routeBreak && routeBreakAPITransformer(routeBreak),
    settings: settings && routeSettingsAPITransformer(settings),
    recurrency: recurrency && routeRecurrencyAPITransformer(recurrency),
    propagation_type: updatePropagationType,
    executed_end_time: executedEndTime,
    executed_start_time: executedStartTime,
  };
};

export const editActivityAPITransformer = (editActivity: EditActivity, routeStartTime: TimestampInMillis): EditActivityAPI | undefined => {
  const { id, type } = editActivity;

  const inputActivity = inputActivityAPITransformer(editActivity, routeStartTime);

  if (inputActivity) {
    return {
      id,
      type,
      input_activity: inputActivity,
    };
  }
};

export const editRouteAPITransformer = (updatedRoute: EditRoute): EditRouteAPI => {
  const { id, name, driverId, deviceId, driverName, vehicleId, activities, settings, recurrency, propagationType } = updatedRoute;

  const editActivityAPIReducer = (updatedActivities: EditActivityAPI[], editActivity: EditActivity) => {
    const editActivityAPI = editActivityAPITransformer(editActivity, settings.workingHours.startTime);
    if (editActivityAPI) {
      updatedActivities.push(editActivityAPI);
    }
    return updatedActivities;
  };

  return {
    id,
    name,
    driver_id: driverId,
    device_id: deviceId,
    driver_name: driverName,
    vehicle_id: vehicleId,
    activities: activities.reduce(editActivityAPIReducer, []),
    settings: routeSettingsAPITransformer(settings),
    recurrency: recurrency ? routeRecurrencyAPITransformer(recurrency) : undefined,
    propagation_type: propagationType,
  };
};

export const parsedActivitiesSpreadsheetRowAPITransformer = (spreadsheetImportFormValues: ActivitiesSpreadsheetImportFormValues): ParsedActivitiesSpreadsheetRowAPI[] => {
  const { selectedFieldColumnsIndexes, selectedTab, selectedHeaderIndex } = spreadsheetImportFormValues;
  return selectedTab && selectedHeaderIndex !== undefined
    ? selectedTab.data.slice(selectedHeaderIndex + 1).map((row) => ({
        address: concatSpreadsheetColumns(row, selectedFieldColumnsIndexes.ADDRESS),
        additional_info: concatSpreadsheetColumns(row, selectedFieldColumnsIndexes.ADDITIONAL_INFO),
        name: concatSpreadsheetColumns(row, selectedFieldColumnsIndexes.NAME),
        time_window: concatSpreadsheetColumns(row, selectedFieldColumnsIndexes.TIME_WINDOW),
        phone_number: concatSpreadsheetColumns(row, selectedFieldColumnsIndexes.PHONE_NUMBER),
        load_size: concatSpreadsheetColumns(row, selectedFieldColumnsIndexes.LOAD_SIZE),
        start_date: concatSpreadsheetColumns(row, selectedFieldColumnsIndexes.START_DATE),
        start_hour: concatSpreadsheetColumns(row, selectedFieldColumnsIndexes.START_HOUR),
      }))
    : [];
};

export const deletedRoutesTransformer = (routesToDelete: DeleteRoutes): DeleteRoutesAPI => {
  const { ids, propagationType } = routesToDelete;

  return {
    ids,
    propagation_type: propagationType,
  };
};

export const activityVehicleAssociationTransformer = (activityVehicleAssociation: ActivityVehicleAssociation): ActivityVehicleAssociationAPI => {
  const { id, vehicleId, deviceId, driverId, driverName } = activityVehicleAssociation;

  return {
    id,
    vehicle_id: vehicleId,
    device_id: deviceId,
    driver_id: driverId,
    driver_name: driverName,
  };
};
